/**
 * BreakdownFailureCard
 *
 * Persistent attention surface inside the Insurance Breakdown modal —
 * sits below the three Basic/Major/Orthodontics finance cards and above
 * the policy-info collapse. Visible whenever at least one of:
 *
 *   1. The most-recent scrape-job for the currently-selected policy
 *      ended in a failure-shape state (`failed` / `timed_out`), OR
 *   2. The patient has a manually-set `manual_attention_note` — captured
 *      by the inline tag-note popover when staff add/edit any tag.
 *
 * Both can coexist; both render as their own section inside the same
 * red-railed card, with the manual note on top (user-curated context
 * comes first, the carrier error follows).
 *
 * The card no longer gates on the "Details Needed by Smilepass" tag — notes are
 * patient-scoped now and surface alongside any tag. New auto-failures
 * still re-tag via the backend signal in
 * `clinics/services/scrape_jobs.py:transition()`.
 *
 * react-redux 6.x — uses connect().
 */

import React from "react";
import { connect } from "react-redux";
import { Button, Input, message } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined, ExclamationCircleFilled, FlagFilled, PlusOutlined, ReloadOutlined } from "@ant-design/icons";

import { selectLatestFailedJobForPolicy } from "../../../../reducers/scrapeJobs";
import { loadViewerRole } from "../../../viewerRole";
import { triggerAIFetch } from "../../lib/aiFetch";
import { controller as rootController } from "../../../../controller";
import { armNextDispatchId, newDispatchId } from "../../../../utils/dispatchTracking";
import { debugLog } from "../../../../utils/debugLog";
import { MISSING_INFO_TAG_NAME } from "../../../InsuranceRequests/requestTypes";

// What a scrape job was fetching, in the words the card uses.
function nounForOperation(operationType) {
  if (operationType === "predetermination") return "estimate";
  if (operationType === "eligibility") return "eligibility";
  return "breakdown";
}

// Collapse the manual note to this many lines; user expands to see the rest.
const NOTE_COLLAPSE_LINES = 2;

function carrierName(job) {
  if (!job || !job.carrier_code) return "";
  const c = String(job.carrier_code).toLowerCase();
  return c.charAt(0).toUpperCase() + c.slice(1);
}

function formatRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return diff + "s ago";
  if (diff < 3600) return Math.floor(diff / 60) + "m ago";
  if (diff < 86400) return Math.floor(diff / 3600) + "h ago";
  return Math.floor(diff / 86400) + "d ago";
}

// Patient carries the "Details Needed by Smilepass" tag? Switches the card/banner
// from the neutral note treatment to the red attention treatment.
// Defensive against either string-or-{name:string} shape.
function hasNeedsAttentionTag(patientTags) {
  if (!patientTags || !patientTags.length) return false;
  for (let i = 0; i < patientTags.length; i++) {
    const v = patientTags[i];
    const name = typeof v === "string" ? v : v && v.name;
    if (name === MISSING_INFO_TAG_NAME) return true;
  }
  return false;
}

export class BreakdownFailureCardInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isRetrying: false,
      noteExpanded: false,
      isEditingNote: false,
      noteDraft: "",
      isSavingNote: false,
      // Optimistic copy after a successful save so the edit reflects
      // immediately without waiting for the parent to refetch the patient.
      localNote: undefined,
      // Whether the collapsed note actually overflows NOTE_COLLAPSE_LINES —
      // measured from the DOM so multi-line short notes are handled too.
      noteOverflows: false,
      // Who is looking (viewerRole). The agent's own words — a failed run's
      // message, a note the agent wrote — are for FTC personnel and super
      // admins; office staff see neither, and may not edit the note (it is
      // Smilepass's ask). Office staff until the role answers.
      viewer: null,
    };
    this.noteBodyRef = React.createRef();
  }

  isFtc() {
    const { viewer } = this.state;
    return !!(viewer && viewer.isFtc);
  }

  // The latest failure, when this viewer may see it.
  visibleFailedJob() {
    return this.isFtc() ? this.props.failedJob : null;
  }

  // The note as this viewer may see it: an agent-sourced note is FTC-only.
  visibleNote() {
    const note = this.effectiveNote();
    if (!note) return note;
    return this.props.manualAttentionNoteSource === "agent" && !this.isFtc() ? "" : note;
  }

  componentDidMount() {
    this.alive = true;
    loadViewerRole().then((viewer) => {
      if (this.alive) this.setState({ viewer });
    });
    this.measureNoteOverflow();
    const el = this.noteBodyRef.current;
    if (el && typeof ResizeObserver !== "undefined") {
      this._noteObserver = new ResizeObserver(() => this.measureNoteOverflow());
      this._noteObserver.observe(el);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.manualAttentionNote !== this.props.manualAttentionNote ||
      prevState.localNote !== this.state.localNote ||
      prevState.noteExpanded !== this.state.noteExpanded ||
      prevState.isEditingNote !== this.state.isEditingNote
    ) {
      this.measureNoteOverflow();
    }
  }

  componentWillUnmount() {
    this.alive = false;
    if (this._noteObserver) {
      this._noteObserver.disconnect();
      this._noteObserver = null;
    }
  }

  // Measure overflow only while the note is collapsed (the only state where
  // it's clamped). When expanded we keep the prior value so the "Show less"
  // control stays visible.
  measureNoteOverflow = () => {
    if (this.state.noteExpanded || this.state.isEditingNote) return;
    const el = this.noteBodyRef.current;
    if (!el) return;
    const overflowing = el.scrollHeight - el.clientHeight > 2;
    if (overflowing !== this.state.noteOverflows) {
      this.setState({ noteOverflows: overflowing });
    }
  };

  // The note we actually display: optimistic local copy wins over the prop.
  effectiveNote() {
    return this.state.localNote !== undefined ? this.state.localNote : this.props.manualAttentionNote;
  }

  startEditNote = () => {
    this.setState({ isEditingNote: true, noteDraft: this.effectiveNote() || "", noteExpanded: true });
  };

  cancelEditNote = () => {
    this.setState({ isEditingNote: false, noteDraft: "" });
  };

  saveNote = async () => {
    // Notes are per-policy now: save against the card's policy (it already
    // knows policyId — it's rendered per selected policy / per failed job).
    const policyId = (this.props.failedJob && this.props.failedJob.policy_id) || this.props.policyId;
    if (!policyId) {
      message.error("Couldn't determine which policy to save the note for.");
      return;
    }
    const next = (this.state.noteDraft || "").trim();
    try {
      this.setState({ isSavingNote: true });
      const res = await rootController.updatePolicyAttentionNote(policyId, next);
      if (res.status >= 200 && res.status < 300) {
        this.setState({ localNote: next || null, isEditingNote: false });
        if (typeof this.props.onNoteSaved === "function") this.props.onNoteSaved(policyId, next || null);
        message.success(next ? "Note updated." : "Note cleared.");
      } else {
        message.error((res.json && (res.json.detail || res.json.error)) || "Failed to save note.");
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to save note.");
    } finally {
      this.setState({ isSavingNote: false });
    }
  };

  handleRetry = async () => {
    const failed = this.props.failedJob;
    const policyId = (failed && failed.policy_id) || this.props.policyId;
    if (!policyId) {
      message.error("Couldn't determine which policy to retry against.");
      return;
    }
    const dispatchId = newDispatchId();
    armNextDispatchId(dispatchId);
    debugLog.info("button", "scrape_button_click", {
      dispatch_id: dispatchId,
      patient_id: this.props.patientId,
      policy_id: policyId,
      reason: "breakdown_failure_card_retry",
    });
    try {
      this.setState({ isRetrying: true });
      const res = await triggerAIFetch(policyId, {});
      debugLog.info("button", "scrape_button_dispatch_response", {
        dispatch_id: dispatchId,
        http_status: res && res.ok ? 200 : 0,
        reason: res && res.ok ? "ok" : (res && res.error) || "unknown",
      });
      if (res.ok) {
        message.success("Retrying — the previous failure will clear when this run finishes.");
      } else {
        message.error(res.error || "Couldn't restart the fetch. Please try again.");
      }
    } finally {
      this.setState({ isRetrying: false });
    }
  };

  renderManualNoteSection() {
    const { manualAttentionNoteSetAt, manualAttentionNoteSource, patientTags, patientId, policyNeedsAttention } = this.props;
    const note = this.visibleNote();
    const { isEditingNote, noteDraft, isSavingNote, noteExpanded } = this.state;
    // When the card is stretched to match the right-side summary (fillHeight),
    // the note clamps to the card's height instead of a fixed line count: it
    // fills the available space, and "Show more" makes the overflow scrollable
    // within the (aligned) card rather than a 2-line clamp.
    const fill = this.props.fillHeight;

    // "Marked for attention" + red icon/body iff the SELECTED POLICY needs
    // attention — per-policy now, so a verified policy doesn't inherit the red
    // header just because a sibling policy failed. Falls back to the patient
    // tag only when the per-policy flag isn't supplied.
    const isAttention = policyNeedsAttention !== undefined
      ? !!policyNeedsAttention
      : hasNeedsAttentionTag(patientTags);
    const iconColor = isAttention ? "#c41d2a" : "#6b43b5";
    const bodyColor = isAttention ? "#5a2024" : "#2a213a";
    const title = isAttention
      ? "Details Needed by Smilepass"
      : manualAttentionNoteSource === "agent" ? "Agent note" : "Note";
    const when = formatRelative(manualAttentionNoteSetAt);
    // The note is Smilepass's: FTC personnel and super admins write it; the
    // office answers through Resubmit / Provide details (the server refuses
    // an office edit too).
    const canEdit = this.isFtc() && !!patientId;

    // Nothing to show and not editing: offer an inline "Add note" affordance
    // only when the card is already visible for another reason (a failure).
    if (!note && !isEditingNote) {
      if (!this.visibleFailedJob() || !canEdit) return null;
      return (
        <div style={{ paddingLeft: 4 }}>
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={this.startEditNote}
            style={{ padding: 0, height: "auto", color: "#6b43b5", fontSize: 12, fontWeight: 600 }}
          >
            Add note
          </Button>
        </div>
      );
    }

    // Clamp whenever not expanded; whether the toggle shows is decided by the
    // measured overflow (noteOverflows), so short multi-line notes are caught.
    const collapsed = !noteExpanded;
    const showToggle = this.state.noteOverflows || noteExpanded;

    return (
      <div style={{ paddingLeft: 4, ...(fill ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : {}) }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <FlagFilled style={{ color: iconColor, fontSize: 16 }} />
          <span style={{ fontWeight: 700, color: "#2a213a", letterSpacing: 0.005, fontSize: 14 }}>
            {title}
          </span>
          {when && !isEditingNote ? (
            <span style={{ color: "#8a8398", fontSize: 12, fontWeight: 500 }}>· {when}</span>
          ) : null}
          {canEdit && !isEditingNote ? (
            <EditOutlined
              onClick={this.startEditNote}
              title="Edit note"
              style={{ marginLeft: "auto", color: "#6b43b5", cursor: "pointer", fontSize: 13 }}
            />
          ) : null}
        </div>

        {isEditingNote ? (
          <div style={{ marginTop: 8 }}>
            <Input.TextArea
              value={noteDraft}
              onChange={(e) => this.setState({ noteDraft: e.target.value })}
              autoSize={{ minRows: 3, maxRows: 10 }}
              placeholder="Add a note for this patient…"
              disabled={isSavingNote}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <Button size="small" onClick={this.cancelEditNote} disabled={isSavingNote}>
                Cancel
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                loading={isSavingNote}
                onClick={this.saveNote}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div
              ref={this.noteBodyRef}
              style={{
                marginTop: 6,
                fontSize: 13,
                lineHeight: 1.45,
                color: bodyColor,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                ...(fill
                  ? { flex: 1, minHeight: 0, overflow: collapsed ? "hidden" : "auto" }
                  : collapsed
                    ? {
                        display: "-webkit-box",
                        WebkitLineClamp: NOTE_COLLAPSE_LINES,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }
                    : {}),
              }}
            >
              {note}
            </div>
            {showToggle ? (
              <Button
                type="link"
                size="small"
                onClick={() => this.setState({ noteExpanded: !noteExpanded })}
                style={{ padding: 0, height: "auto", marginTop: 4, color: "#6b43b5", fontSize: 12, fontWeight: 600, flexShrink: 0, alignSelf: "flex-start" }}
              >
                {collapsed ? "Show more" : "Show less"}
              </Button>
            ) : null}
          </>
        )}
      </div>
    );
  }

  // Our team's Details Needed checklist on this policy and, once it came, the
  // office's reply — office-safe, so everyone sees it under the note.
  renderAskSection() {
    const ask = this.props.infoAsk;
    if (!ask || !Array.isArray(ask.fields) || (!ask.fields.length && !ask.other_text)) return null;
    const asked = ask.fields.map((f) => f.label).concat(ask.other_text ? ["Other: " + ask.other_text] : []);
    const answered = !!ask.answered_at;
    const replies = answered ? (ask.answers || []).map((a) => {
      const value = a.value && typeof a.value === "object"
        ? [a.value.address, a.value.city, a.value.state, a.value.zip_code].filter(Boolean).join(", ")
        : a.value;
      return a.label + ": " + (a.skipped || value == null || value === "" ? "not provided" : value);
    }) : [];
    return (
      <div style={{ paddingLeft: 4, marginTop: 8, fontSize: 12.5, lineHeight: 1.5, color: "#4a4759" }} data-testid="info-ask">
        <div>
          <span style={{ fontWeight: 600 }}>Asked for: </span>
          {asked.join(", ")}
        </div>
        {answered ? (
          <div style={{ marginTop: 4 }}>
            <span style={{ fontWeight: 600 }}>{`Office replied ${formatRelative(ask.answered_at)}: `}</span>
            {replies.join(" · ") || "nothing provided"}
            {ask.answer_note ? <div style={{ fontStyle: "italic", marginTop: 2 }}>{`“${ask.answer_note}”`}</div> : null}
          </div>
        ) : null}
      </div>
    );
  }

  renderFailedSection() {
    const failedJob = this.visibleFailedJob();
    if (!failedJob) return null;
    // A host that knows what it fetches names it ("estimate" on the Estimates
    // tab). Otherwise the job says: the selector is per POLICY, not per
    // operation, so a tab that fetches nothing (Claims) can still be shown a
    // breakdown or estimate failure and must not call it its own.
    const noun = this.props.noun || nounForOperation(failedJob.operation_type);
    const carrier = carrierName(failedJob);
    const when = formatRelative(failedJob.completed_at || failedJob.updated_at);
    const errorMsg =
      failedJob.error_message ||
      "The latest fetch failed but no message was returned by the carrier.";
    const errorCode = failedJob.error_code;
    return (
      <div style={{ paddingLeft: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ExclamationCircleFilled style={{ color: "#c41d2a", fontSize: 18 }} />
          <span style={{ fontWeight: 700, color: "#2a213a", letterSpacing: 0.005, fontSize: 14 }}>
            Latest {noun} fetch failed
          </span>
          {carrier ? (
            <span style={{ color: "#8a8398", fontSize: 12, fontWeight: 500 }}>· {carrier}</span>
          ) : null}
          {when ? (
            <span style={{ color: "#8a8398", fontSize: 12, fontWeight: 500 }}>· {when}</span>
          ) : null}
          <Button
            type="primary"
            size="small"
            icon={<ReloadOutlined />}
            loading={this.state.isRetrying}
            onClick={this.handleRetry}
            style={{
              marginLeft: "auto",
              background: "#c41d2a",
              borderColor: "#c41d2a",
            }}
          >
            Retry
          </Button>
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            lineHeight: 1.5,
            color: "#5a2024",
          }}
        >
          {errorMsg}
        </div>
        {errorCode ? (
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: "#8a8398",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          >
            {errorCode}
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const { patientTags, policyNeedsAttention } = this.props;
    const failedJob = this.visibleFailedJob();
    const note = this.visibleNote();
    // The card surfaces (a) the latest fetch failure for the selected
    // policy and (b) the patient's manual note. Either is enough to
    // render. Also stay mounted while the user is actively editing/adding
    // a note even if the persisted value is momentarily empty.
    const ask = this.props.infoAsk;
    const hasAsk = !!(ask && ((ask.fields && ask.fields.length) || ask.other_text));
    if (!failedJob && !note && !hasAsk && !this.state.isEditingNote) return null;

    const showBothSections = !!failedJob && (!!note || this.state.isEditingNote);
    // Red attention treatment when there's a real escalation signal —
    // a failed scrape, or the patient explicitly tagged Details Needed by Smilepass.
    // A plain note on its own gets the neutral purple treatment so it
    // doesn't read as an alarm.
    const isAlarming = !!failedJob || (policyNeedsAttention !== undefined
      ? !!policyNeedsAttention
      : hasNeedsAttentionTag(patientTags));
    const border = isAlarming ? "1px solid #ffd5d1" : "1px solid #e1d9f3";
    const background = isAlarming ? "#fffafa" : "#faf7ff";
    const railColor = isAlarming ? "#c41d2a" : "#6b43b5";
    const dividerColor = isAlarming
      ? "rgba(196, 29, 42, 0.18)"
      : "rgba(107, 67, 181, 0.18)";

    return (
      <div
        style={{
          marginTop: 10,
          padding: "9px 12px",
          borderRadius: 10,
          border,
          background,
          position: "relative",
          overflow: "hidden",
          ...(this.props.fillHeight
            ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", boxSizing: "border-box" }
            : {}),
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: railColor,
          }}
        />
        {this.renderManualNoteSection()}
        {this.renderAskSection()}
        {showBothSections ? (
          <div
            aria-hidden="true"
            style={{
              height: 1,
              background: dividerColor,
              margin: "12px 0",
              marginLeft: 4,
            }}
          />
        ) : null}
        {this.renderFailedSection()}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  failedJob: selectLatestFailedJobForPolicy(state, ownProps.policyId),
  policyId: ownProps.policyId,
  patientId: ownProps.patientId,
  patientTags: ownProps.patientTags,
  manualAttentionNote: ownProps.manualAttentionNote,
  manualAttentionNoteSetAt: ownProps.manualAttentionNoteSetAt,
  manualAttentionNoteSource: ownProps.manualAttentionNoteSource,
  infoAsk: ownProps.infoAsk,
});

export default connect(mapStateToProps)(BreakdownFailureCardInner);
